import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  SlackChannelsResponse,
  SlackInstallStatus,
  SlackOAuthStartResponse,
} from './slack-integrations-client.type';

const axiosInstance = AxiosInstance();
const baseURL = `${appConfig.VITE_APP_API_URL}/integrations/slack`;

/**
 * Fetch the Slack authorize URL that begins the org workspace install flow.
 *
 * The BE returns the URL as JSON (not a 302) so the call can carry our JWT
 * `Authorization` header — top-level navigation cannot, which previously
 * forced the user to also hold a Django session (admin console login).
 *
 * @param {string} organizationId - The organization initiating the install.
 * @returns {Promise<string>} The Slack authorize URL to navigate the popup to.
 */
function getInstallStartUrl(organizationId: string): Promise<string> {
  const url = `${baseURL}/install/start`;
  return axiosInstance
    .get<SlackOAuthStartResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data.url)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to start Slack install';
      console.error('[Slack] getInstallStartUrl error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * Fetch the Slack authorize URL that begins the per-user OAuth flow.
 *
 * @param {string} organizationId - The organization the user is connecting to.
 * @returns {Promise<string>} The Slack authorize URL to navigate the popup to.
 */
function getUserAuthStartUrl(organizationId: string): Promise<string> {
  const url = `${baseURL}/user-auth/start`;
  return axiosInstance
    .get<SlackOAuthStartResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data.url)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to start Slack connect';
      console.error('[Slack] getUserAuthStartUrl error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * Fetch install + per-user-connect status for an organization.
 *
 * @param {string} organizationId - The organization to check.
 * @returns {Promise<SlackInstallStatus>} Status object including installed/team_id/team_name/bot_user_id/user_connected.
 */
function getSlackStatus(organizationId: string): Promise<SlackInstallStatus> {
  const url = `${baseURL}/status`;
  return axiosInstance
    .get<SlackInstallStatus>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to fetch Slack status';
      console.error('[Slack] getSlackStatus error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * List Slack channels visible to the current user in the org's workspace.
 *
 * Requires the user to have completed per-user OAuth — the BE returns 409
 * Conflict when the user has not connected their Slack account.
 *
 * @param {string} organizationId - The organization whose workspace to list channels in.
 * @returns {Promise<SlackChannelsResponse>} Channels list.
 */
function getSlackChannels(organizationId: string): Promise<SlackChannelsResponse> {
  const url = `${baseURL}/channels`;
  return axiosInstance
    .get<SlackChannelsResponse>(url, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const message = error.response?.data?.detail || error.message || 'Failed to load Slack channels';
      console.error('[Slack] getSlackChannels error:', message, error.response?.data);
      throw new Error(message);
    });
}

/**
 * Soft-delete the org's Slack installation (admin only).
 *
 * Flips `is_active=False` server-side; preserves the audit trail. The user's
 * SlackUserAuth rows are not deleted but become unusable since the install
 * is inactive.
 *
 * @param {string} organizationId - The organization whose install to remove.
 * @returns {Promise<void>} Resolves when the install has been deactivated.
 */
async function uninstallSlack(organizationId: string): Promise<void> {
  const url = `${baseURL}/install`;
  await axiosInstance.delete(url, { params: { organization_id: organizationId } }).catch((error) => {
    const message = error.response?.data?.detail || error.message || 'Failed to uninstall Slack';
    console.error('[Slack] uninstallSlack error:', message, error.response?.data);
    throw new Error(message);
  });
}

/**
 * Disconnect the current user's Slack account from the given org.
 *
 * @param {string} organizationId - The organization whose user-auth to soft-delete.
 * @returns {Promise<void>} Resolves when the user-auth row has been soft-deleted.
 */
async function disconnectSlackUser(organizationId: string): Promise<void> {
  const url = `${baseURL}/user-auth`;
  await axiosInstance.delete(url, { params: { organization_id: organizationId } }).catch((error) => {
    const message = error.response?.data?.detail || error.message || 'Failed to disconnect your Slack account';
    console.error('[Slack] disconnectSlackUser error:', message, error.response?.data);
    throw new Error(message);
  });
}

export {
  disconnectSlackUser,
  getInstallStartUrl,
  getSlackChannels,
  getSlackStatus,
  getUserAuthStartUrl,
  uninstallSlack,
};
